'use client'

import { AnimatePresence, motion } from 'motion/react'
import { Fragment, ReactNode, useEffect, useId, useRef, useState } from 'react'
import { useMeasure } from 'react-use'
import { useImmer } from 'use-immer'
import { LoadingSpinner } from '@/components/loading-spinner'
import { IOnClickEvent } from '@/typings/onclick-event'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'

export interface IDropdownMenuItem<T> {
    label?: string
    onClick?: (value: T) => void | Promise<unknown>
    icon?: ReactNode
    id: string | number
    value: T
    color?: string
    node?: ReactNode
    selected?: boolean
}

type DropdownMenuProps<T> = {
    isOpen: boolean
    className?: string
    setIsOpen: (isOpen: boolean) => void
    align?: 'left' | 'right'
} & ({ items: IDropdownMenuItem<T>[] | undefined } | { renderContent: () => ReactNode })

export function DropdownMenu<T>(props: DropdownMenuProps<T>) {
    const componentId = useId()

    const [contentRef, { height: contentHeight }] = useMeasure<HTMLDivElement>()

    const [loadingStates, produceLoadingStates] = useImmer<Record<string, boolean>>({})

    const [contentHeightAnimationType, setContentHeightAnimationType] = useState<'auto' | 'height'>(
        'auto'
    )

    const hasRunRef = useRef(false)
    // This is because the initial scale animation distorts the height of the content
    // so we need to wait for the animation to finish before we can set the height
    useEffect(() => {
        if (hasRunRef.current) return

        hasRunRef.current = true
        const timeout = setTimeout(() => {
            setContentHeightAnimationType('height')
        }, 3000)

        return () => {
            clearTimeout(timeout)
        }
    }, [])

    if ('items' in props && !props.items?.length) return null
    return (
        <AnimatePresence mode="sync">
            {props.isOpen && (
                // This is to close the dropdown when clicking outside of it
                <div
                    className="fixed min-h-dvh min-w-[100vw] left-0 top-0"
                    onClick={(e: IOnClickEvent) => {
                        e.stopPropagation()
                        e.preventDefault()

                        props.setIsOpen(false)
                    }}
                />
            )}

            {props.isOpen && (
                <motion.div
                    key={'dropdown-menu' + componentId}
                    initial={{
                        opacity: 0,
                        scale: 0.98,
                        height:
                            contentHeightAnimationType === 'auto' ? 'fit-content' : contentHeight,
                    }}
                    animate={{
                        opacity: 1,
                        scale: 1,
                        height:
                            contentHeightAnimationType === 'auto' ? 'fit-content' : contentHeight,
                    }}
                    exit={{
                        opacity: 0,
                        scale: 0.98,
                    }}
                    transition={{
                        opacity: { duration: 0.12 },
                        scale: { duration: 0.15, ease: 'easeOut' },
                    }}
                    className={cn(
                        `flex flex-col text-center absolute right-0 top-1 rounded-s bg-white origin-top
                        overflow-hidden min-w-[180px] z-navigation shadow-custom`,
                        props.className
                    )}
                >
                    <div ref={contentRef}>
                        <div className="p-1 gap-y-0.5">
                            {run(() => {
                                if ('renderContent' in props) return props.renderContent()

                                return props.items?.map((item) => {
                                    const renderCustom =
                                        item.node &&
                                        !item.icon &&
                                        !item.label &&
                                        !item.onClick &&
                                        !item.color

                                    if (renderCustom)
                                        return (
                                            <div key={item.id + 'dropdown-menu'}>{item.node}</div>
                                        )

                                    return (
                                        <motion.div
                                            initial={{ backgroundColor: '#fff' }}
                                            animate={{ backgroundColor: '#fff' }}
                                            whileHover={{ backgroundColor: '#ededed' }}
                                            transition={{ duration: 0.02, ease: 'easeOut' }}
                                            onClick={() => {
                                                const res = item.onClick?.(item.value)

                                                if (res instanceof Promise) {
                                                    produceLoadingStates((draft) => {
                                                        draft[item.id] = true
                                                    })

                                                    res.finally(() => {
                                                        produceLoadingStates((draft) => {
                                                            draft[item.id] = false
                                                        })

                                                        props.setIsOpen(false)
                                                    })

                                                    return
                                                }

                                                props.setIsOpen(false)
                                            }}
                                            key={(item.label ?? item.id) + 'dropdown-menu'}
                                            className="whitespace-nowrap cursor-pointer flex items-center py-2.5 px-2 bg-white w-full rounded-s"
                                            style={{
                                                justifyContent:
                                                    !props.align || props.align === 'left'
                                                        ? 'flex-start'
                                                        : 'flex-end',
                                            }}
                                        >
                                            {run(() => {
                                                if (item.node) return item.node

                                                return (
                                                    <Fragment>
                                                        {item.icon && (
                                                            <div className="flex items-center justify-center h-4 w-4 mr-2">
                                                                {loadingStates[item.id] ? (
                                                                    <LoadingSpinner
                                                                        color={
                                                                            item.color ?? 'black'
                                                                        }
                                                                        size={16}
                                                                    />
                                                                ) : (
                                                                    item.icon
                                                                )}
                                                            </div>
                                                        )}

                                                        <p
                                                            className={cn(
                                                                'text-right',
                                                                item.selected && 'font-semibold'
                                                            )}
                                                            style={{
                                                                color: item.color ?? COLORS.black,
                                                            }}
                                                        >
                                                            {item.label}
                                                        </p>
                                                    </Fragment>
                                                )
                                            })}
                                        </motion.div>
                                    )
                                })
                            })}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
